<template>
  <div>
    <template v-if="campaignId == 0">
      <b-container
        v-if="wrapper == null"
        class="container-small"
      >
        <b-skeleton-img
          no-aspect
          height="150px"
        />
      </b-container>
      <Choose
        v-else
        :campaign-id="campaignId"
        :ad-group-id="adGroupId"
      />
    </template>
    <template v-else>
      <template v-if="loading">
        <b-container class="container-700px">
          <b-card class="mb-1">
            <b-skeleton width="20%" />
            <b-skeleton height="35px" />
            <b-skeleton width="20%" />
            <b-skeleton height="35px" />
            <b-skeleton
              width="20%"
              class="mt-3"
            />
            <b-skeleton width="40%" />
            <b-row>
              <b-col
                v-for="i in 3"
                :key="i"
                sm="4"
              >
                <b-skeleton height="70px" />
              </b-col>
            </b-row>
            <b-skeleton
              width="20%"
              class="mt-2"
            />
            <b-skeleton width="40%" />
            <b-row>
              <b-col
                v-for="i in 4"
                :key="i"
                sm="6"
              >
                <b-skeleton height="120px" />
              </b-col>
            </b-row>
            <b-skeleton
              width="20%"
              class="mt-1"
            />
            <b-row>
              <b-col
                v-for="i in 2"
                :key="i"
                sm="6"
              >
                <b-skeleton height="20px" />
              </b-col>
            </b-row>
            <b-skeleton
              height="40px"
              class="mt-1"
            />
          </b-card>
        </b-container>
      </template>
      <section v-else-if="wrapper != null && wrapper.model != null">
        <scroll-container
          class="pt-2 mt-n30px"
          :height-offset="adGroupId > 0 ? '55px' : heightOffset"
        >
          <b-container class="container-700px">
            <!-- #region Start of ad group name, type and ad subtype -->
            <b-card class="mb-1">
              <div class="mb-2">
                <b-card-title
                  v-if="wrapper.model.id === 0"
                  class="mb-0"
                >
                  {{ $tk('ClientPortal.Form.CreateAdGroup') }}
                </b-card-title>
                <b-card-title
                  v-else
                  class="mb-0"
                >
                  {{ $tk('ClientPortal.Form.EditAdGroup') }}
                </b-card-title>
                <b-breadcrumb class="m-0 p-0">
                  <b-breadcrumb-item :to="`/${$route.params.accountId}/campaign/edit/${wrapper.campaign.id}`">
                    {{ wrapper.campaign.name }}
                  </b-breadcrumb-item>
                  <b-breadcrumb-item
                    v-if="wrapper.model.id === 0"
                    active
                  >
                    {{ $tk('ClientPortal.Form.NewAdGroup') }}
                  </b-breadcrumb-item>
                  <b-breadcrumb-item
                    v-else
                    active
                  >
                    {{ wrapper.model.name }}
                  </b-breadcrumb-item>
                </b-breadcrumb>
                <hr class="m-0 mt-1">
              </div>
              <b-form-group
                id="Name"
                class="mb-3"
                :label="$tk('ClientPortal.Form.NameOfAdGroup') + ' *'"
                label-for="input-ad-group-name"
                :invalid-feedback="errors['Name']"
                :state="errors['Name'] == null ? null : false"
              >
                <b-form-input
                  id="input-ad-group-name"
                  v-model="wrapper.model.name"
                  placeholder="Enter ad group name here..."
                />
              </b-form-group>
              <template>
                <b-card-title class="mb-50">
                  {{ $tk('ClientPortal.Form.AdType') }}
                </b-card-title>
                <b-card-text>
                  {{ $tk('ClientPortal.Form.AdType.Info') }}
                </b-card-text>
                <b-row class="mx-n25">
                  <b-col
                    v-for="adType in wrapper.adTypes"
                    :key="'adType-' + adType.id"
                    class="px-25"
                  >
                    <b-card
                      class="text-center border mb-0"
                      :class="[getAdTypeButtonClass(adType.id), wrapper.model.id ? '' : 'cursor-pointer' ]"
                      @click="wrapper.model.id ? '' : setType(adType)"
                    >
                      <GjIcon
                        class="mb-50"
                        :name="getAdTypeIconTemporarily(adType)"
                        size="30px"
                      />
                      <small class="d-block">
                        {{ adType.name }}
                      </small>
                    </b-card>
                  </b-col>
                </b-row>
              </template>
              <template v-if="wrapper.model.adTypeId > 0">
                <b-card-title
                  class="mb-50 mt-2"
                >
                  {{ $tk('ClientPortal.Form.AdSubtype') }}
                </b-card-title>
                <b-card-text>
                  {{ $tk('ClientPortal.Form.AdSubtype.Info') }}
                </b-card-text>
                <b-row
                  v-if="wrapper.model.adTypeId > 0"
                  class="mx-n25"
                >
                  <b-col
                    v-for="adSubType in wrapper.adType.subtypes"
                    :key="'adSubType-' + adSubType.id"
                    class="px-25"
                    sm="6"
                  >
                    <b-card
                      class="text-center border mb-50 "
                      :class="[getAdSubtypeButtonClass(adSubType.id), wrapper.model.id ? '' : 'cursor-pointer']"
                      @click="wrapper.model.id ? '' : setSubtype(adSubType)"
                    >
                      <b-img
                        :src="getAdSubtypeIcon(adSubType)"
                        :alt="adSubType.name"
                      />
                      <b-card-text :class="getAdSubtypeTextClass(adSubType.id)">
                        {{ adSubType.name }}
                      </b-card-text>
                    </b-card>
                  </b-col>
                </b-row>
              </template>
            </b-card>
            <!-- #endregion -->
            <template v-if="wrapper.model.adSubtypeId > 0">
              <!-- #region Start of bid strategy -->
              <b-card
                class="mb-1"
              >
                <b-card-title class="mb-50">
                  {{ $tk('ClientPortal.Form.BidStrategy') }}
                  <GjIcon
                    v-b-tooltip.hover="$tk('ClientPortal.Form.BidStrategyTooltip')"
                    name="Info"
                  />
                </b-card-title>
                <b-row class="mx-n50">
                  <b-col
                    v-for="(adSubtypeBidStrategy, adSubtypeBidStrategyIndex) in wrapper.adSubtype.bidStrategies"
                    :key="'adSubtypeBidStrategy-' +adSubtypeBidStrategyIndex"
                    class="px-50"
                    sm="3"
                  >
                    <b-button
                      variant="outline-dark"
                      :class="getAdSubtypeBidStrategyButtonClass(adSubtypeBidStrategy.bidStrategy.id)"
                      class="d-flex justify-content-between align-items-center mb-50"
                      block
                      @click="setSubtypeBidStrategy(adSubtypeBidStrategy)"
                    >
                      {{ adSubtypeBidStrategy.bidStrategy.name }}
                      <b-form-radio
                        v-model="wrapper.model.bidStrategyId"
                        class="radio-btn"
                        :value="adSubtypeBidStrategy.bidStrategy.id"
                        name="adGroupBidStrategy"
                      />
                    </b-button>
                  </b-col>
                </b-row>
                <b-form-group
                  v-if="wrapper.model.bidStrategyId > 0"
                  id="BidValue"
                  class="mb-0 mt-1"
                  :invalid-feedback="errors['BidValue']"
                  :state="errors['BidValue'] == null ? null : false"
                >
                  <template #description>
                    <span v-html="getBidValueText()"></span>
                  </template>
                  <label>
                    {{ $tk('ClientPortal.Form.Price') }}
                    <GjIcon
                      v-b-tooltip.hover="$tk('ClientPortal.Form.PriceTooltip')"
                      name="Info"
                    />
                  </label>
                  <b-row class="mx-n50">
                    <b-col
                      class="px-50"
                      sm="4"
                    >
                      <b-input-group
                        class="input-group-merge"
                      >
                        <b-form-input
                          v-model.number="wrapper.model.bidValue"
                          type="number"
                          value="0"
                          :min="minimumBidValue"
                          :max="maximumBidValue || null"
                        />
                        <b-input-group-append
                          is-text
                        >
                          {{ $currencySymbol }}
                        </b-input-group-append>
                      </b-input-group>
                    </b-col>
                  </b-row>
                </b-form-group>
              </b-card>
              <!-- #endregion -->
              <template v-if="wrapper.model.bidStrategyId > 0">
                <b-alert
                  class="p-1"
                  show
                  variant="primary"
                >
                  <GjIcon
                    class="mr-50"
                    name="Info"
                  />
                  {{ $tk('ClientPortal.Form.FrequencyCap.BestTargetingResultsInfo') }}
                </b-alert>
                <!-- #region Start of frequency cap--->
                <b-card
                  class="mb-1"
                >
                  <b-row>
                    <b-col>
                      <b-card-title class="mb-50">
                        {{ $tk('ClientPortal.Form.FrequencyCap') }}
                        <GjIcon
                          v-b-tooltip.hover="$tk('ClientPortal.Form.FrequencyCapTooltip')"
                          name="Info"
                        />
                      </b-card-title>
                    </b-col>
                    <b-col class="text-right">
                      <b-card-title class="mb-50 text-muted">
                        {{ $tk('ClientPortal.Form.Optional') }}
                      </b-card-title>
                    </b-col>
                  </b-row>
                  <b-card-text class="">
                    {{ $tk('ClientPortal.Form.FrequencyCap.Info') }}
                    <!-- Specify the frequency of ad display to one user -->
                  </b-card-text>
                  <b-form-row
                    v-for="(frequencyCap, index) in wrapper.model.frequencyCaps"
                    :key="'frequencyCap' + index"
                    class="mb-2"
                  >
                    <b-col
                      sm="3"
                      class="mb-50"
                    >
                      <v-select
                        v-model="frequencyCap.frequencyCapEventId"
                        :clearable="false"
                        label="name"
                        :placeholder="$tk('ClientPortal.AdGroup.ChooseFrequencyCap')"
                        :options="wrapper.frequencyCapEvents"
                        :reduce="event => event.id"
                      />
                    </b-col>
                    <div
                      class="mb-50"
                    >
                      <b-form-input
                        v-model.number="frequencyCap.frequencyCapValue"
                        type="number"
                        min="1"
                        class="number__input-width"
                      />
                    </div>
                    <b-col sm="3">
                      <v-select
                        v-model="frequencyCap.frequencyCapPeriodId"
                        :clearable="false"
                        label="name"
                        :placeholder="$tk('ClientPortal.AdGroup.ChooseFrequencyCapPeriod')"
                        :options="wrapper.frequencyCapPeriods"
                        :reduce="period => period.id"
                      />
                    </b-col>
                    <b-col
                      sm="1"
                      class="py-50 text-center"
                    >
                      {{ $tk('ClientPortal.Form.Per') }}
                    </b-col>
                    <div class="icon-position__wrapper">
                      <v-select
                        v-model="frequencyCap.frequencyCapEntityId"
                        :clearable="false"
                        label="name"
                        class="smaller-indicator"
                        :placeholder="$tk('ClientPortal.AdGroup.ChooseFrequencyCapEntity')"
                        :options="wrapper.frequencyCapEntities"
                        :reduce="entity => entity.id"
                      />
                    </div>
                    <b-col
                      sm="1"
                      class="pt-1 pt-sm-50 d-flex flex-row-reverse flex-sm-row"
                    >
                      <b-button
                        variant="link"
                        size="sm"
                        @click="removeFrequencyCap(index)"
                      >
                        <GjIcon
                          class="cursor-pointer text-secondary"
                          name="Delete"
                        />
                      </b-button>
                      <b-button
                        variant="link"
                        size="sm"
                        @click="copyFrequencyCap(frequencyCap)"
                      >
                        <GjIcon
                          class="cursor-pointer text-secondary"
                          name="Copy"
                        />
                      </b-button>
                    </b-col>
                  </b-form-row>
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    @click="addFrequencyCap"
                  >
                    <GjIcon
                      class="mr-50"
                      name="Plus"
                    />
                    {{ $tk('ClientPortal.Form.Add') }}
                    <span
                      v-if="wrapper.model.frequencyCaps.length > 0"
                      class="ml-25"
                    >
                      {{ $tk('ClientPortal.Form.More') }}
                    </span>
                  </b-button>
                </b-card>
                <!--  #endregion End of frequency cap--->
                <!-- #region Start of geographic targeting--->
                <b-card class="mb-1">
                  <b-row>
                    <b-col>
                      <b-card-title class="mb-50">
                        {{ $tk('ClientPortal.Form.GeographyTargeting') }}
                      </b-card-title>
                    </b-col>
                    <b-col class="text-right">
                      <b-card-title class="mb-50 text-muted">
                        {{ $tk('ClientPortal.Form.Optional') }}
                      </b-card-title>
                    </b-col>
                  </b-row>
                  <b-card-text class="mb-2">
                    {{ $tk('ClientPortal.Form.GeographyTargeting.Info') }}
                    <!-- Target users based on their location -->
                  </b-card-text>
                  <b-form-group
                    v-if="geoTargetingProperty"
                    class="mb-2"
                  >
                    <label>
                      {{ $tk('ClientPortal.Form.GeographyInclude') }}
                      <GjIcon
                        v-b-tooltip.hover="$tk('ClientPortal.Form.GeographyIncludeTooltip')"
                        name="Info"
                      />
                    </label>
                    <MultiselectTags
                      id="geographyInclude"
                      :disabled="loading || !wrapper"
                      :included-tags="includedCountries"
                      :options="geoOptionGroups"
                      :placeholder="$tk('ClientPortal.Form.SearchGeo')"
                      icon="Location"
                      @open="loadGeoTargetingOptions"
                      @includeTag="includeTargetingOption"
                      @removeTag="removeTargetingOption"
                    />
                  </b-form-group>
                  <b-form-group
                    class="mb-2"
                  >
                    <label>
                      {{ $tk('ClientPortal.Form.GeographyExclude') }}
                      <GjIcon
                        v-b-tooltip.hover="$tk('ClientPortal.Form.GeographyExcludeTooltip')"
                        name="Info"
                      />
                    </label>
                    <MultiselectTags
                      id="geographyExclude"
                      :disabled="loading || !wrapper"
                      :included-tags="excludedCountries"
                      :options="geoOptionGroups"
                      :placeholder="$tk('ClientPortal.Form.SearchGeo')"
                      icon="Location"
                      @open="loadGeoTargetingOptions"
                      @includeTag="excludeTargetingOption"
                      @removeTag="removeTargetingOption"
                    />
                  </b-form-group>
                  <!-- #region Start of city targeting -->
                  <b-form-group
                    v-if="cityTargetingProperty"
                    class="mb-2"
                  >
                    <label>
                      {{ $tk('ClientPortal.Form.GeographyIncludeCities') }}
                      <GjIcon
                        v-b-tooltip.hover="$tk('ClientPortal.Form.GeographyIncludeCitiesTooltip')"
                        name="Info"
                      />
                    </label>
                    <MultiselectTags
                      id="citiesInclude"
                      :disabled="loading || !wrapper"
                      :included-tags="includedCities"
                      :options="cityTargetingOptions"
                      :placeholder="$tk('ClientPortal.Form.SearchCities')"
                      icon="InstitutionBuildingOffice"
                      @open="loadCityTargetingOptions"
                      @includeTag="includeTargetingOption"
                      @removeTag="removeTargetingOption"
                    />
                  </b-form-group>
                  <b-form-group
                    v-if="cityTargetingProperty"
                    class="mb-0"
                  >
                    <label>
                      {{ $tk('ClientPortal.Form.GeographyExcludeCities') }}
                      <GjIcon
                        v-b-tooltip.hover="$tk('ClientPortal.Form.GeographyExcludeCitiesTooltip')"
                        name="Info"
                      />
                    </label>
                    <MultiselectTags
                      id="citiesExclude"
                      :disabled="loading || !wrapper"
                      :included-tags="excludedCities"
                      :options="cityTargetingOptions"
                      :placeholder="$tk('ClientPortal.Form.SearchCities')"
                      icon="InstitutionBuildingOffice"
                      @open="loadCityTargetingOptions"
                      @includeTag="excludeTargetingOption"
                      @removeTag="removeTargetingOption"
                    />
                  </b-form-group>
                  <!-- #endregion End of city targeting-->
                </b-card>
                <!-- #endregion End of geographic targeting--->
                <b-card class="mb-1">
                  <b-row>
                    <b-col>
                      <b-card-title class="mb-50">
                        {{ $tk('ClientPortal.Form.Targeting') }}
                        <!-- <GjIcon
                          v-b-tooltip.hover="$tk('ClientPortal.Form.Targeting.Info')"
                          name="Info"
                        /> -->
                      </b-card-title>
                    </b-col>
                    <b-col class="text-right">
                      <b-card-title class="mb-50 text-muted">
                        {{ $tk('ClientPortal.Form.Optional') }}
                      </b-card-title>
                    </b-col>
                  </b-row>
                  <b-card-text class="mb-2">
                    {{ $tk('ClientPortal.Form.Targeting.Info') }}
                    <!-- Choose targeting on devices, operating systems and browsers -->
                  </b-card-text>
                  <!-- #region Start of devices targeting--->
                  <b-card-text class="mb-50">
                    {{ $tk('ClientPortal.Form.Devices') }}
                    <GjIcon
                      v-b-tooltip.hover="$tk('ClientPortal.Form.DevicesTooltip')"
                      name="Info"
                    />
                  </b-card-text>
                  <hr class="mt-50 mb-1">
                  <b-row
                    v-for="(option, optionIndex) in deviceOptions"
                    :key="'selectedDevice-' + optionIndex"
                    class="mb-1"
                  >
                    <b-col sm="3">
                      <b-form-checkbox
                        class="mt-50"
                        :checked="option.selected == true"
                        @change="checkTargetingOption(option, true)"
                      >
                        {{ option.option }}
                      </b-form-checkbox>
                    </b-col>
                    <b-col sm="3">
                      <b-input-group
                        :key="optionIndex"
                        class="input-group-merge"
                      >
                        <b-form-input
                          :ref="'input'+option.id"
                          v-model.number="deviceBidAdjustments[option.id].value"
                          :disabled="!option.selected"
                          :formatter="adjustInput"
                          type="number"
                          value="0"
                          step="10"
                          min="0"
                          max="100"
                          @input="checkTotalValueSelected(optionIndex, option.id, deviceBidAdjustments[option.id].value)"
                          @blur="checkActiveDevices(option.id)"
                        />
                        <b-input-group-append
                          is-text
                        >
                          %
                        </b-input-group-append>
                      </b-input-group>
                    </b-col>
                  </b-row>
                  <!-- #endregion End of devices targeting--->
                  <!-- #region Start of platforms targeting--->
                  <b-card-text class="mb-50">
                    {{ $tk('ClientPortal.Form.Platforms') }}
                    <GjIcon
                      v-b-tooltip.hover="$tk('ClientPortal.Form.PlatformsTooltip')"
                      name="Info"
                    />
                  </b-card-text>
                  <hr class="mt-50 mb-1">
                  <b-form-checkbox
                    v-for="(option, optionIndex) in platformOptions"
                    :key="'selectedPlatform-' + optionIndex"
                    class="mb-2"
                    :checked="option.selected === true"
                    @change="checkTargetingOption(option)"
                  >
                    {{ option.option }}
                  </b-form-checkbox>
                  <!-- #endregion End of platforms targeting--->
                  <!-- #region Start of OS targeting--->
                  <b-form-group
                    v-if="osTargetingProperty"
                    class="mb-2"
                  >
                    <label>
                      {{ $tk('ClientPortal.Form.IncludeOS') }}
                      <GjIcon
                        v-b-tooltip.hover="$tk('ClientPortal.Form.IncludeOSTooltip')"
                        name="Info"
                      />
                    </label>
                    <MultiselectTags
                      id="osInclude"
                      :disabled="loading || !wrapper"
                      :included-tags="includedOperatingSystems"
                      :options="osOptionGroups"
                      :placeholder="$tk('ClientPortal.Form.SearchOS')"
                      icon="MonitorScreenDashboard"
                      @open="loadOsTargetingOptions"
                      @includeTag="includeTargetingOption"
                      @removeTag="removeTargetingOption"
                    />
                  </b-form-group>
                  <b-form-group
                    v-if="osTargetingProperty"
                    class="mb-3"
                  >
                    <label>
                      {{ $tk('ClientPortal.Form.ExcludeOS') }}
                      <GjIcon
                        v-b-tooltip.hover="$tk('ClientPortal.Form.ExcludeOSTooltip')"
                        name="Info"
                      />
                    </label>
                    <MultiselectTags
                      id="osExclude"
                      :disabled="loading || !wrapper"
                      :included-tags="excludedOperatingSystems"
                      :options="osOptionGroups"
                      :placeholder="$tk('ClientPortal.Form.SearchOS')"
                      icon="MonitorScreenDashboard"
                      @open="loadOsTargetingOptions"
                      @includeTag="excludeTargetingOption"
                      @removeTag="removeTargetingOption"
                    />
                  </b-form-group>
                  <!-- #endregion End of OS targeting--->
                  <!-- #region Start of Browser targeting--->
                  <b-form-group
                    v-if="browserTargetingProperty"
                    class="mb-2"
                  >
                    <label>
                      {{ $tk('ClientPortal.Form.IncludeBrowsers') }}
                      <GjIcon
                        v-b-tooltip.hover="$tk('ClientPortal.Form.IncludeBrowsersTooltip')"
                        name="Info"
                      />
                    </label>
                    <MultiselectTags
                      id="browserInclude"
                      :disabled="loading || !wrapper"
                      :included-tags="includedBrowsers"
                      :options="browserOptionGroups"
                      :placeholder="$tk('ClientPortal.Form.SearchBrowsers')"
                      icon="Globe"
                      @open="loadBrowserTargetingOptions"
                      @includeTag="includeTargetingOption"
                      @removeTag="removeTargetingOption"
                    />
                  </b-form-group>
                  <b-form-group
                    v-if="browserTargetingProperty"
                    class="mb-0"
                  >
                    <label>
                      {{ $tk('ClientPortal.Form.ExcludeBrowsers') }}
                      <GjIcon
                        v-b-tooltip.hover="$tk('ClientPortal.Form.ExcludeBrowsersTooltip')"
                        name="Info"
                      />
                    </label>
                    <MultiselectTags
                      id="browserExclude"
                      :disabled="loading || !wrapper"
                      :included-tags="excludedBrowsers"
                      :options="browserOptionGroups"
                      :placeholder="$tk('ClientPortal.Form.SearchBrowsers')"
                      icon="Globe"
                      @open="loadBrowserTargetingOptions"
                      @includeTag="excludeTargetingOption"
                      @removeTag="removeTargetingOption"
                    />
                  </b-form-group>
                  <!-- #endregion End of Browser targeting--->
                </b-card>
                <!-- #endragion End of Targeting -->
                <!-- #region Start of Audience and Placement Targeting-->
                <b-card class="mb-1">
                  <b-row>
                    <b-col>
                      <b-card-title class="mb-50">
                        {{ $tk('ClientPortal.Form.AudienceAndPlacementTargeting') }}
                        <!-- <GjIcon
                          v-b-tooltip.hover="$tk('ClientPortal.Form.AudienceAndPlacementTargeting.Info')"
                          name="Info"
                        /> -->
                      </b-card-title>
                    </b-col>
                    <b-col class="text-right">
                      <b-card-title class="mb-50 text-muted">
                        {{ $tk('ClientPortal.Form.Optional') }}
                      </b-card-title>
                    </b-col>
                  </b-row>
                  <b-card-text class="mb-2">
                    {{ $tk('ClientPortal.Form.AudienceAndPlacementTargeting.Info') }}
                  </b-card-text>
                  <b-form-group>
                    <label>
                      {{ $tk('ClientPortal.Form.AudienceAndPlacementTargeting.SpecifikLink') }}
                      <GjIcon
                        v-b-tooltip.hover="$tk('ClientPortal.Form.AudienceAndPlacementTargeting.SpecifikLinkTooltip')"
                        name="Info"
                      />
                    </label>
                    <b-form-row>
                      <b-col sm="4">
                        <v-select
                          v-model.number="wrapper.model.specificLinkType"
                          :clearable="false"
                          label="text"
                          class="smaller-indicator"
                          :placeholder="$tk('ClientPortal.AdGroup.ChooseLinkType')"
                          :options="linkTypes"
                          :reduce="linkType => linkType.value"
                        />
                      </b-col>
                      <b-col sm="8">
                        <b-form-input
                          v-model="wrapper.model.specificLink"
                          type="text"
                          :placeholder="$tk('ClientPortal.Form.SpecificLink.PlaceholderLink')"
                        />
                      </b-col>
                    </b-form-row>
                  </b-form-group>
                  <b-form-group
                    v-if="audienceTargetingProperty && audienceTargetingProperty.optionGroups.length > 0"
                    class="mb-2"
                  >
                    <label>
                      {{ $tk('ClientPortal.Form.AudienceAndPlacementTargeting.IncludeAudiences') }}
                      <GjIcon
                        v-b-tooltip.hover="$tk('ClientPortal.Form.AudienceAndPlacementTargeting.IncludeAudiencesTooltip')"
                        name="Info"
                      />
                    </label>
                    <MultiselectTags
                      id="audienceInclude"
                      :included-tags="includedAudiences"
                      :options="audienceOptionGroups"
                      :placeholder="$tk('ClientPortal.Form.SearchAudience')"
                      icon="AccountProfileUsersGroup"
                      @open="loadAudienceTargetingOptions"
                      @includeTag="includeTargetingOption"
                      @removeTag="removeTargetingOption"
                    />
                  </b-form-group>
                  <b-form-group
                    v-if="audienceTargetingProperty && audienceTargetingProperty.optionGroups.length > 0"
                    class="mb-2"
                  >
                    <label>
                      {{ $tk('ClientPortal.Form.AudienceAndPlacementTargeting.ExcludeAudiences') }}
                      <GjIcon
                        v-b-tooltip.hover="$tk('ClientPortal.Form.AudienceAndPlacementTargeting.ExcludeAudiencesTooltip')"
                        name="Info"
                      />
                    </label>
                    <MultiselectTags
                      id="audienceExclude"
                      :included-tags="excludedAudiences"
                      :options="audienceOptionGroups"
                      :placeholder="$tk('ClientPortal.Form.SearchAudience')"
                      icon="AccountProfileUsersGroup"
                      @open="loadAudienceTargetingOptions"
                      @includeTag="excludeTargetingOption"
                      @removeTag="removeTargetingOption"
                    />
                  </b-form-group>
                </b-card>
                <!-- #endregion End of Audience and Placement Targeting -->
                <b-card class="mb-1">
                  <b-row>
                    <b-col>
                      <b-card-title class="mb-50">
                        {{ $tk('ClientPortal.Form.ContentTargeting') }}
                      </b-card-title>
                    </b-col>
                    <b-col class="text-right">
                      <b-card-title class="mb-50 text-muted">
                        {{ $tk('ClientPortal.Form.Optional') }}
                      </b-card-title>
                    </b-col>
                  </b-row>
                  <b-card-text class="mb-2">
                    {{ $tk('ClientPortal.Form.ContentTargeting.Info') }}
                  </b-card-text>
                  <b-dropdown
                    v-if="subTypeCatalogs.length > 0"
                    class="mb-2 dropdown-no-scroll"
                    size="sm"
                    variant="outline-primary"
                  >
                    <template v-slot:button-content>
                      <span class="d-lg-inline-block">
                        <GjIcon
                          class="mr-50"
                          name="Plus"
                        />
                        {{ $tk('ClientPortal.Form.Add') }}
                      </span>
                    </template>
                    <b-dropdown-item
                      v-for="(catalog, index) in subTypeCatalogs"
                      :key="'catalog-'+index"
                      @click="addCatalog(catalog)"
                    >
                      {{ catalog.name }}
                    </b-dropdown-item>
                  </b-dropdown>
                  <b-dropdown
                    v-if="wrapper.targetingProperties && wrapper.targetingProperties.filter(x=> x.subtypeMapped).length > 0"
                    class="mb-2 ml-xs-0 ml-sm-1 dropdown-no-scroll"
                    size="sm"
                    variant="outline-primary"
                  >
                    <template v-slot:button-content>
                      <span class="d-lg-inline-block">
                        <GjIcon
                          class="mr-50"
                          name="Plus"
                        />
                        {{ $tk('ClientPortal.Form.AddCustomTargetingProperty') }}
                      </span>
                    </template>
                    <b-dropdown-item
                      v-for="(targetingProperty, index) in wrapper.targetingProperties.filter(x=> x.subtypeMapped)"
                      :key="'targetingProperty-'+index"
                      @click="addTargetingProperty(targetingProperty)"
                    >
                      {{ targetingProperty.name }}
                    </b-dropdown-item>
                  </b-dropdown>
                  <div
                    v-for="(catalog) in wrapper.catalogs.filter(x=> x.show && x.subtypeMapped)"
                    :key="'catalog'+catalog.id"
                    class="mb-3"
                  >
                    <b-form-row class="mb-50">
                      <b-col
                        cols="10"
                        sm="11"
                      >
                        <h4 class="card-title mb-0">
                          {{ catalog.name }}
                        </h4>
                      </b-col>
                      <b-col
                        cols="2"
                        sm="1"
                      >
                        <b-button
                          variant="link"
                          size="sm"
                          @click="removeCatalog(catalog)"
                        >
                          <GjIcon
                            class="text-secondary"
                            name="Close"
                          />
                        </b-button>
                      </b-col>
                    </b-form-row>
                    <b-form-group class="mb-2">
                      <label>
                        {{ $tk('ClientPortal.Form.ContentTargeting.Include') }}
                      </label>
                      <multiselect
                        label="title"
                        track-by="title"
                        :options="catalog.availableItems"
                        :custom-label="customLabel"
                        :show-labels="false"
                        :option-height="73"
                        :loading="loadingCatalogItems"
                        :placeholder="$tk('ClientPortal.Form.SearchTargetingContent')"
                        :internal-search="false"
                        @focus.stop="loadCatalogItems(catalog)"
                        @search-change="(query) => searchCatalogItems(catalog, query)"
                        @select="(selectedCatalogItem, id) => addCatalogItem(catalog, selectedCatalogItem, true)"
                      >
                        <template slot="noOptions">
                          {{ $tk('ClientPortal.Form.SearchForTargetingContent') }}
                        </template>
                        <template
                          slot="option"
                          slot-scope="props"
                        >
                          <b-form-row>
                            <b-col
                              sm="1"
                              class="mr-2"
                            >
                              <div
                                class="selected-item-thumbnail d-inline-block"
                                :style="{ backgroundImage: 'url(' + props.option.contentImageUrl + ')' }"
                              />
                            </b-col>
                            <b-col
                              sm="10"
                              class="d-flex"
                            >
                              <div class="option__title d-inline-block align-self-center">
                                {{ props.option.contentName }}
                              </div>
                            </b-col>
                          </b-form-row>
                        </template>
                        <template
                          slot="caret"
                          slot-scope="{ toggle }"
                        >
                          <div
                            class="multiselect__select"
                            @mousedown.prevent.stop="toggle()"
                          >
                            <GjIcon
                              class="icon-wrapper"
                              name="ArrowDown_fill"
                              size="21"
                            />
                          </div>
                        </template>
                      </multiselect>
                      <b-card
                        v-if="catalog.selectedItems.filter(x => x.include).length"
                        class="border mt-50 include-exclude-card"
                        no-body
                      >
                        <b-card-body class="p-1">
                          <div
                            v-for="catalogItem, catalogItemIndex in catalog.selectedItems.filter(x=> x.include)"
                            :key="'catalogItem'+catalogItem.id"
                          >
                            <b-form-row>
                              <b-col sm="2">
                                <div
                                  class="selected-item-thumbnail d-inline-block"
                                  :style="{ backgroundImage: 'url(' + catalogItem.contentImageUrl + ')' }"
                                />
                              </b-col>
                              <b-col
                                sm="9"
                                class="d-flex"
                              >
                                <div class="align-self-center">
                                  {{ catalogItem.contentName }}
                                </div>
                              </b-col>
                              <b-col
                                class="pt-50"
                                sm="1"
                              >
                                <b-button
                                  variant="link-secondary"
                                  size="sm"
                                  @click="removeCatalogItem(catalog, catalogItem.id)"
                                >
                                  <GjIcon name="Delete" />
                                </b-button>
                              </b-col>
                            </b-form-row>
                            <hr
                              v-if="catalogItemIndex < catalog.selectedItems.filter(x=> x.include).length-1"
                              class="item-divider"
                            >
                          </div>
                        </b-card-body>
                      </b-card>
                    </b-form-group>
                    <b-form-group class="mb-2">
                      <label>
                        {{ $tk('ClientPortal.Form.ContentTargeting.Exclude') }}
                      </label>
                      <multiselect
                        label="title"
                        track-by="title"
                        :options="catalog.availableItems"
                        :option-height="73"
                        :custom-label="customLabel"
                        :show-labels="false"
                        :loading="loadingCatalogItems"
                        :placeholder="$tk('ClientPortal.Form.SearchTargetingContent')"
                        :internal-search="false"
                        @focus.stop="loadCatalogItems(catalog)"
                        @search-change="(query) => searchCatalogItems(catalog, query)"
                        @select="(selectedCatalogItem, id) => addCatalogItem(catalog, selectedCatalogItem, false)"
                      >
                        <template slot="noOptions">
                          {{ $tk('ClientPortal.Form.SearchForTargetingContent') }}
                        </template>
                        <template
                          slot="option"
                          slot-scope="props"
                        >
                          <b-form-row>
                            <b-col
                              sm="1"
                              class="mr-2"
                            >
                              <div
                                class="selected-item-thumbnail d-inline-block"
                                :style="{ backgroundImage: 'url(' + props.option.contentImageUrl + ')' }"
                              />
                            </b-col>
                            <b-col
                              sm="10"
                              class="d-flex"
                            >
                              <div class="option__title d-inline-block align-self-center">
                                {{ props.option.contentName }}
                              </div>
                            </b-col>
                          </b-form-row>
                        </template>
                        <template
                          slot="caret"
                          slot-scope="{ toggle }"
                        >
                          <div
                            class="multiselect__select"
                            @mousedown.prevent.stop="toggle()"
                          >
                            <GjIcon
                              class="icon-wrapper"
                              name="ArrowDown_fill"
                              size="21"
                            />
                          </div>
                        </template>
                      </multiselect>
                      <b-card
                        v-if="catalog.selectedItems.filter(x => !x.include).length"
                        class="border mt-50 include-exclude-card"
                        no-body
                      >
                        <b-card-body class="p-1">
                          <div
                            v-for="catalogItem, catalogItemIndex in catalog.selectedItems.filter(x=> !x.include)"
                            :key="'catalogItem'+catalogItem.id"
                          >
                            <b-form-row>
                              <b-col sm="2">
                                <div
                                  class="selected-item-thumbnail d-inline-block"
                                  :style="{ backgroundImage: 'url(' + catalogItem.contentImageUrl + ')' }"
                                />
                              </b-col>
                              <b-col
                                sm="9"
                                class="d-flex"
                              >
                                <div class="align-self-center">
                                  {{ catalogItem.contentName }}
                                </div>
                              </b-col>
                              <b-col
                                class="pt-50"
                                sm="1"
                              >
                                <b-button
                                  variant="link-secondary"
                                  size="sm"
                                  @click="removeCatalogItem(catalog, catalogItem.id)"
                                >
                                  <GjIcon name="Delete" />
                                </b-button>
                              </b-col>
                            </b-form-row>
                            <hr
                              v-if="catalogItemIndex < catalog.selectedItems.filter(x=> !x.include).length-1"
                              class="item-divider"
                            >
                          </div>
                        </b-card-body>
                      </b-card>
                    </b-form-group>
                  </div>
                  <div
                    v-for="(targetingProperty, index) in wrapper.targetingProperties"
                    :key="'targetingPropertyInclude' + index"
                    class="feed-type-wrapper mb-3"
                  >
                    <template v-if="targetingProperty.show && targetingProperty.subtypeMapped">
                      <hr>
                      <b-form-row class="mb-50">
                        <b-col
                          cols="10"
                          sm="11"
                        >
                          <h4 class="card-title mb-0">
                            {{ targetingProperty.name }}
                          </h4>
                        </b-col>
                        <b-col
                          cols="2"
                          sm="1"
                        >
                          <b-button
                            variant="link"
                            size="sm"
                            @click="removeTargetingProperty(index)"
                          >
                            <GjIcon
                              class="text-secondary"
                              name="Close"
                            />
                          </b-button>
                        </b-col>
                      </b-form-row>
                      <b-form-group class="mb-2">
                        <label>
                          {{ $tk('ClientPortal.Form.ContentTargeting.Include') }}
                        </label>
                        <MultiselectTags
                          v-if="targetingProperty.type === targetingPropertyType.PREDEFINED"
                          id="contentTargetingInclude"
                          :included-tags="targetingProperty.selectedOptions.filter(x=> x.include)"
                          :options="targetingProperty.availableOptions"
                          :placeholder="$tk('ClientPortal.Form.SearchTargetingOptions')"
                          option-label="name"
                          track-by="value"
                          :group="false"
                          :targeting-property="targetingProperty"
                          @addOption="addTargetingOption"
                          @removeOption="removeCustomTargetingOption"
                        />
                        <MultiselectTags
                          v-else
                          id="contentTargetingInclude"
                          :included-tags="targetingProperty.selectedOptions.filter(x=> x.include)"
                          :options="targetingProperty.availableOptions"
                          :placeholder="$tk('ClientPortal.Form.SearchTargetingOptions')"
                          option-label="name"
                          track-by="value"
                          :group="false"
                          :targeting-property="targetingProperty"
                          @addOption="addNewTargetingOption"
                          @removeOption="removeCustomTargetingOption"
                        />
                      </b-form-group>
                      <b-form-group class="mb-3">
                        <label>
                          {{ $tk('ClientPortal.Form.ContentTargeting.Exclude') }}
                        </label>
                        <MultiselectTags
                          v-if="targetingProperty.type === targetingPropertyType.PREDEFINED"
                          id="contentTargetingExclude"
                          :included-tags="targetingProperty.selectedOptions.filter(x => !x.include)"
                          :options="targetingProperty.availableOptions"
                          :placeholder="$tk('ClientPortal.Form.SearchTargetingOptions')"
                          option-label="name"
                          track-by="value"
                          :group="false"
                          :targeting-property="targetingProperty"
                          :include="false"
                          @addOption="addTargetingOption"
                          @removeOption="removeCustomTargetingOption"
                        />
                        <MultiselectTags
                          v-else
                          id="contentTargetingExclude"
                          :included-tags="targetingProperty.selectedOptions.filter(x => !x.include)"
                          :options="targetingProperty.availableOptions"
                          :placeholder="$tk('ClientPortal.Form.SearchTargetingOptions')"
                          option-label="name"
                          track-by="value"
                          :group="false"
                          :targeting-property="targetingProperty"
                          :include="false"
                          @addOption="addNewTargetingOption"
                          @removeOption="removeCustomTargetingOption"
                        />
                      </b-form-group>
                    </template>
                  </div>
                  <b-form-group
                    v-if="monetizationTargetingProperty"
                    class="mb-2"
                  >
                    <label>
                      {{ $tk('ClientPortal.Form.PropertyWhiteList') }}
                      <GjIcon
                        v-b-tooltip.hover="$tk('ClientPortal.Form.PropertyWhiteListTooltip')"
                        name="Info"
                      />
                    </label>
                    <MultiselectTags
                      id="propertyInclude"
                      :included-tags="includedWhiteList"
                      :options="monetizationOptionGroups"
                      :placeholder="$tk('ClientPortal.Form.SearchProperty')"
                      icon="BoxStorageArchive"
                      @open="loadMonetizationTargetingOptions"
                      @includeTag="includeTargetingOption"
                      @removeTag="removeTargetingOption"
                    />
                  </b-form-group>
                  <b-form-group
                    v-if="monetizationTargetingProperty"
                    class="mb-2"
                  >
                    <label>
                      {{ $tk('ClientPortal.Form.PropertyBlackList') }}
                      <GjIcon
                        v-b-tooltip.hover="$tk('ClientPortal.Form.PropertyBlackListTooltip')"
                        name="Info"
                      />
                    </label>
                    <MultiselectTags
                      id="propertyExclude"
                      :included-tags="includedBlackList"
                      :options="monetizationOptionGroups"
                      :placeholder="$tk('ClientPortal.Form.SearchProperty')"
                      icon="BoxStorageArchive"
                      @open="loadMonetizationTargetingOptions"
                      @includeTag="excludeTargetingOption"
                      @removeTag="removeTargetingOption"
                    />
                  </b-form-group>
                  <!-- Include Language -->
                  <b-form-group
                    v-if="monetizationTargetingProperty"
                    class="mb-2"
                  >
                    <label>
                      {{ $tk('ClientPortal.Form.PropertyIncludeLanguage') }}
                      <!-- <GjIcon
                        v-b-tooltip.hover="$tk('')"
                        name="Info"
                      /> -->
                    </label>
                    <MultiselectTags
                      id="propertyInclude"
                      :included-tags="includedLanguages"
                      :options="languageOptionGroups"
                      :placeholder="$tk('ClientPortal.Form.SearchLanguages')"
                      icon="Language"
                      @open="loadLanguageTargetingOptions"
                      @includeTag="includeTargetingOption"
                      @removeTag="removeTargetingOption"
                    />
                  </b-form-group>
                  <!-- Exclude Language -->
                  <b-form-group
                    v-if="monetizationTargetingProperty"
                    class="mb-2"
                  >
                    <label>
                      {{ $tk('ClientPortal.Form.PropertyExcludeLanguage') }}
                      <!-- <GjIcon
                        v-b-tooltip.hover="$tk('ClientPortal.Form.PropertyBlackListTooltip')"
                        name="Info"
                      /> -->
                    </label>
                    <MultiselectTags
                      id="propertyInclude"
                      :included-tags="excludedLanguages"
                      :options="languageOptionGroups"
                      :placeholder="$tk('ClientPortal.Form.SearchLanguages')"
                      icon="Language"
                      @open="loadLanguageTargetingOptions"
                      @includeTag="excludeTargetingOption"
                      @removeTag="removeTargetingOption"
                    />
                  </b-form-group>
                </b-card>
                <!-- #endregion End of Content Targeting -->

                <b-card class="mb-1">
                  <b-row>
                    <b-col>
                      <b-card-title class="mb-50">
                        {{ $tk('ClientPortal.Form.TargetWithSurveysAnswers') }}
                      </b-card-title>
                    </b-col>
                    <b-col class="text-right">
                      <b-card-title class="mb-50 text-muted">
                        {{ $tk('ClientPortal.Form.Optional') }}
                      </b-card-title>
                    </b-col>
                  </b-row>
                  <b-card-text class="mb-2">
                    {{ $tk('ClientPortal.Form.ContentTargeting.UsersAnswersForTargeting') }}
                  </b-card-text>
                  <b-form-group
                    v-if="surveyTargetingProperty"
                    class="mb-2"
                  >
                    <label class="mb-50">
                      {{ $tk('ClientPortal.Form.Survey') }}
                    </label>
                    <multiselect
                      :options="surveyTargetingProperty"
                      :value="surveyTargeting"
                      :multiple="false"
                      :searchable="true"
                      :close-on-select="true"
                      :clear-on-select="true"
                      :preserve-search="true"
                      :option-height="48"
                      group-values="options"
                      group-label="name"
                      :group-select="false"
                      label="option"
                      track-by="id"
                      :custom-label="customLabel"
                      select-label=""
                      selected-label=""
                      deselect-label=""
                      :placeholder="$tk('ClientPortal.Form.SelectTheSurvey')"
                      @focus.stop=""
                      @select="(selectedSrv) => includeSurveyTargetingOption(selectedSrv)"
                      @remove="(selectedSrv) => removeSurvey(selectedSrv)"
                      @open="loadSurveyTargetingOptions"
                    >
                      <template
                        slot="singleLabel"
                      > {{ $tk('ClientPortal.Form.SelectTheSurvey') }}
                      </template>
                      <template
                        slot="option"
                        slot-scope="props"
                      >
                        <div class="option__desc">
                          <span
                            v-if="props.option.$groupLabel"
                          >
                            <GjIcon
                              class="mr-50"
                              name="Article"
                            />
                            {{ props.option.$groupLabel }}
                          </span>
                          <span
                            v-else
                            class="option__title ml-1 d-flex justify-content-between"
                            :class="surveyTargeting.some(survey => survey.surveyName===props.option.option && survey.id === props.option.id) ? 'selected-option' : ''"
                          >
                            <span>
                              <GjIcon
                                class="mr-50"
                                name="Article"
                              />
                              {{ props.option.option }}
                            </span>
                            <GjIcon
                              v-if="surveyTargeting.some(survey => survey.surveyName === props.option.option && survey.id === props.option.id)"
                              name="Check"
                              class="mr-50"
                            />
                          </span>
                        </div>
                      </template>
                      <template
                        slot="caret"
                        slot-scope="{ toggle }"
                      >
                        <div
                          class="multiselect__select"
                          @mousedown.prevent.stop="toggle()"
                        >
                          <GjIcon
                            class="icon-wrapper"
                            name="ArrowDown_fill"
                            size="21"
                          />
                        </div>
                      </template>
                    </multiselect>
                  </b-form-group>
                  <b-card
                    v-for="survey in surveyTargeting"
                    :key="survey.id"
                    class="card-background"
                  >
                    <b-card-title>
                      <b-form-row>
                        <b-col>
                          {{ survey.surveyName }}
                        </b-col>
                        <b-col
                          class="text-right"
                          sm="1"
                        >
                          <b-button
                            variant="link-secondary"
                            size="sm"
                            @click="removeSurvey(survey)"
                          >
                            <GjIcon name="Close_fill" />
                          </b-button>
                        </b-col>
                      </b-form-row>
                    </b-card-title>
                    <label class="mb-50">
                      {{ $tk('ClientPortal.Form.Questions') }}
                    </label>
                    <multiselect
                      :id="survey.id"
                      :options="survey.selectedSurvey"
                      :close-on-select="true"
                      :preserve-search="true"
                      group-values="answers"
                      class="mb-50"
                      group-label="name"
                      :label="$tk('ClientPortal.Form.SelectTheAnswers')"
                      track-by="id"
                      :option-height="40"
                      :custom-label="customLabel"
                      :show-labels="true"
                      select-label=""
                      selected-label=""
                      deselect-label=""
                      @focus.stop=""
                      @select="(selectedOption, id) => includeSurveyAnswers(selectedOption, id)"
                    >
                      <template slot="placeholder">
                        <span class="multiselect__placeholder mb-0">{{ $tk('ClientPortal.Form.SelectTheAnswers') }}</span>
                      </template>
                      <template
                        slot="option"
                        slot-scope="props"
                      >
                        <div class="option__desc">
                          <span
                            v-if="props.option.$groupLabel"
                            class="option__title option__title--disabled"
                          >
                            {{ props.option.$groupLabel }}
                          </span>
                          <span
                            v-else
                            class="option__title ml-1 d-flex justify-content-between"
                            :class="survey.selectedAnswers.some(answer => answer.answer===props.option.answer && answer.questionId === props.option.questionId && answer.id === props.option.id) ? 'selected-option' : ''"
                          >
                            <span>
                              {{ props.option.answer }}
                            </span>
                            <span>
                              <GjIcon
                                v-if="survey.selectedAnswers.some(answer => answer.answer===props.option.answer && answer.questionId === props.option.questionId && answer.id === props.option.id)"
                                name="Check"
                                class="mr-50"
                              />
                            </span>
                          </span>
                        </div>
                      </template>
                      <template
                        slot="caret"
                        slot-scope="{ toggle }"
                      >
                        <div
                          class="multiselect__select"
                          @mousedown.prevent.stop="toggle()"
                        >
                          <GjIcon
                            class="icon-wrapper"
                            name="ArrowDown_fill"
                            size="21"
                          />
                        </div>
                      </template>
                    </multiselect>
                    <b-card
                      v-if="survey.selectedAnswers.length"
                      class="survey-card mb-0"
                      body-class="mt-0"
                    >
                      <SelectedOption
                        ref="childComponent"
                        :survey="survey"
                        :survey-targeting="surveyTargeting"
                      />
                    </b-card>
                  </b-card>
                </b-card>
                <!-- End of Target with survey answers -->
                <b-card class="mb-1">
                  <b-row>
                    <b-col>
                      <b-card-title class="mb-50">
                        {{ $tk('ClientPortal.Form.BoostOptimization') }}
                        <GjIcon
                          v-b-tooltip.hover="{ customClass: 'boostOptimization-tooltip', }"
                          :title="$tk('ClientPortal.Form.EnableBoostOptimizationTooltip')"
                          name="Info"
                        />
                      </b-card-title>
                    </b-col>
                    <b-col class="text-right">
                      <b-card-title class="mb-50 text-muted">
                        {{ $tk('ClientPortal.Form.Optional') }}
                      </b-card-title>
                    </b-col>
                  </b-row>
                  <b-card-text class="mb-2">
                    {{ $tk('ClientPortal.Form.BoostOptimization.Info') }}
                  </b-card-text>
                  <b-form-group
                    class="mb-2"
                  >
                    <label>
                      {{ $tk('ClientPortal.Form.EnableOptimization') }}
                    </label>
                    <b-form-checkbox
                      v-model="wrapper.model.boostAds"
                      class="d-block"
                      checked="false"
                      name="check-button"
                      switch
                    />
                  </b-form-group>
                </b-card>
                <b-card class="mb-1">
                  <b-row>
                    <b-col>
                      <b-card-title class="mb-50">
                        {{ $tk('ClientPortal.Form.AdScheduling') }}
                        <!-- <GjIcon
                          v-b-tooltip.hover="$tk('ClientPortal.Form.AdScheduling.Info')"
                          name="Info"
                        /> -->
                      </b-card-title>
                    </b-col>
                    <b-col class="text-right">
                      <b-card-title class="mb-50 text-muted">
                        {{ $tk('ClientPortal.Form.Optional') }}
                      </b-card-title>
                    </b-col>
                  </b-row>
                  <b-card-text class="mb-2">
                    {{ $tk('ClientPortal.Form.AdScheduling.Info') }}
                    <!-- Select dates and times when ad is running -->
                  </b-card-text>
                  <b-form-row
                    v-for="(schedule, index) in wrapper.model.schedules"
                    :key="'schedule-' + index"
                    class="mb-2"
                  >
                    <b-col
                      sm="4"
                    >
                      <v-select
                        v-model="schedule.typeId"
                        :clearable="false"
                        label="name"
                        class="smaller-indicator"
                        :placeholder="$tk('ClientPortal.AdGroup.ChooseFrequencyCapEntity')"
                        :options="wrapper.scheduleTypes"
                        :reduce="schedule => schedule.id"
                      />
                    </b-col>
                    <b-col
                      class="pt-50 text-right"
                      sm="1"
                    >
                      <small>
                        {{ $tk('ClientPortal.Form.AdScheduling.From') }}
                      </small>
                    </b-col>
                    <b-col
                      sm="2"
                    >
                      <vue-timepicker
                        v-model="schedule.hourFrom"
                      />
                    </b-col>
                    <b-col
                      class="pt-50 text-center"
                      sm="1"
                    >
                      <small>
                        {{ $tk('ClientPortal.Form.AdScheduling.To') }}
                      </small>
                    </b-col>
                    <b-col
                      sm="2"
                    >
                      <vue-timepicker
                        v-model="schedule.hourTo"
                      />
                    </b-col>
                    <b-col
                      class="pt-1 pt-sm-50 text-right"
                      sm="2"
                    >
                      <b-button
                        variant="link"
                        size="sm"
                        @click="removeSchedule(index)"
                      >
                        <GjIcon
                          class="cursor-pointer text-secondary"
                          name="Delete"
                        />
                      </b-button>
                      <b-button
                        variant="link"
                        size="sm"
                        @click="copySchedule(schedule)"
                      >
                        <GjIcon
                          class="cursor-pointer text-secondary"
                          name="Copy"
                        />
                      </b-button>
                    </b-col>
                  </b-form-row>
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    @click="addSchedule"
                  >
                    <GjIcon
                      class="mr-50"
                      name="Plus"
                    />
                    <span
                      v-if="wrapper.model.schedules.length > 0"
                      class="ml-25"
                    >
                      {{ $tk('ClientPortal.Form.AddMore') }}
                    </span>
                    <span
                      v-else
                      class="ml-25"
                    >
                      {{ $tk('ClientPortal.Form.Add') }}
                    </span>
                  </b-button>
                </b-card>
              </template>
            </template>
          </b-container>
        </scroll-container>
        <div class="border-top text-right py-1">
          <div class="container-700px mx-auto">
            <b-button
              v-if="adGroupId > 0 && entity "
              class="bg-white"
              variant="outline-dark"
              size="sm"
              :disabled="saving"
              @click="closeEditModal"
            >
              {{ $tk('ClientPortal.Form.Actions.Cancel') }}
            </b-button>
            <b-button
              v-else-if="adGroupId > 0 && !entity "
              class="bg-white"
              variant="outline-dark"
              size="sm"
              :disabled="saving"
              @click="cancelAdGroupEdit"
            >
              {{ $tk('ClientPortal.Form.Actions.Cancel') }}
            </b-button>
            <b-button
              v-else
              class="bg-white"
              size="sm"
              variant="outline-dark"
              @click="cancelAdGroup"
            >
              {{ $tk('ClientPortal.Form.Actions.Cancel') }}
            </b-button>
            <b-button
              class="ml-50"
              size="sm"
              variant="primary"
              :disabled="!isFormComplete"
              @click="saveAdGroup"
            >
              {{ adGroupId > 0 ? $tk('ClientPortal.Form.Actions.Save') : $tk('ClientPortal.Form.Actions.CreateAdGroup') }}
            </b-button>
          </div>
        </div>
        <b-modal
          id="navigation-modal"
          ref="navigation-modal"
          centered
          :title="`Are you sure you want to navigate away?`"
        >
          <p>{{ modalMessage }}</p>
          <template #modal-footer="{ cancel }">
            <b-button
              variant="outline-secondary"
              @click="cancel()"
            >
              No
            </b-button>
            <b-button
              variant="danger"
              @click="confirm"
            >
              Yes
            </b-button>
          </template>
        </b-modal>
      </section>
    </template>

  </div>
</template>

<script>
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import Multiselect from 'vue-multiselect'
import AdGroupService from '@/api/client/adgroups.service'
import SurveysService from '@/api/client/surveys.service'
import CatalogsService from '@/api/client/catalogs.service'
import ScrollContainer from '@/components/ScrollContainer.vue'
import VueTimepicker from 'vue2-timepicker'
import notification from '@/mixins/toastification'
import scrollToError from '@/mixins/scrollToError'
import { ref } from '@vue/composition-api'
import vSelect from 'vue-select'
import { formatCurrency, ceilToCurrency, floorToCurrency } from '@/utils/currency'
import Choose from './Choose.vue'
import SelectedOption from './SelectedOption.vue'
import MultiselectTags from './MultiselectTags.vue'

const SurveyPrivacyType = {
  Private: 1,
  Shared: 2,
  Public: 3,
}

export default {
  components: {
    Choose,
    GjIcon,
    Multiselect,
    ScrollContainer,
    VueTimepicker,
    SelectedOption,
    MultiselectTags,
    vSelect,
  },
  mixins: [notification],
  inject: ['closeEditModal'],
  props: {
    campaignId: Number,
    adGroupId: Number,
    heightOffset: {
      type: String,
      default: '125px',
    },
    entity: Object,
  },

  data() {
    return {
      wrapper: null,
      errors: [],
      devicesModified: [],
      currentTotalDevicePercent: null,
      loadingCatalogItems: false,
      loading: false,
      saving: false,
      selectedOption: '',
      adType: null,
      linkTypes: [
        { value: 1, text: this.$tk('ClientPortal.Form.SpecificLink.StartsWith') },
        { value: 2, text: this.$tk('ClientPortal.Form.SpecificLink.EndsWith') },
        { value: 3, text: this.$tk('ClientPortal.Form.SpecificLink.Contains') },
        { value: 4, text: this.$tk('ClientPortal.Form.SpecificLink.FullMatch') },
      ],
      modalMessage: 'Are you sure you want to navigate away? Your changes will be lost.',
      geoOptionGroups: [],
      cityTargetingOptions: [],
      osOptionGroups: [],
      browserOptionGroups: [],
      audienceOptionGroups: [],
      monetizationOptionGroups: [],
      languageOptionGroups: [],
      surveyTargetingProperty: [],
    }
  },
  computed: {
    // options
    platformOptions() {
      return this.wrapper.targetingDict.platform.optionGroups[0].options
    },
    deviceOptions() {
      return this.wrapper.targetingDict.device.optionGroups[0].options
    },
    deviceBidAdjustments() {
      return this.wrapper.model.deviceBidAdjustments
    },

    // countries
    geoTargetingProperty() {
      return this.wrapper && this.wrapper.targetingDict
        ? this.wrapper.targetingDict.country
        : null
    },
    includedCountries() {
      return this.getSelectedOptions(this.geoTargetingProperty, true)
    },
    excludedCountries() {
      return this.getSelectedOptions(this.geoTargetingProperty, false)
    },

    // cities
    cityTargetingProperty() {
      return this.wrapper && this.wrapper.targetingDict
        ? this.wrapper.targetingDict.city
        : null
    },

    includedCities() {
      return this.getSelectedOptions(this.cityTargetingProperty, true)
    },

    excludedCities() {
      return this.getSelectedOptions(this.cityTargetingProperty, false)
    },

    // operating system
    osTargetingProperty() {
      return this.wrapper && this.wrapper.targetingDict
        ? this.wrapper.targetingDict.operatingsystem
        : null
    },
    includedOperatingSystems() {
      return this.getSelectedOptions(this.osTargetingProperty, true)
    },
    excludedOperatingSystems() {
      return this.getSelectedOptions(this.osTargetingProperty, false)
    },

    // browser
    browserTargetingProperty() {
      return this.wrapper && this.wrapper.targetingDict
        ? this.wrapper.targetingDict.browser
        : null
    },
    includedBrowsers() {
      return this.getSelectedOptions(this.browserTargetingProperty, true)
    },
    excludedBrowsers() {
      return this.getSelectedOptions(this.browserTargetingProperty, false)
    },

    // audience
    audienceTargetingProperty() {
      return this.wrapper && this.wrapper.targetingDict
        ? this.wrapper.targetingDict.audience
        : null
    },
    includedAudiences() {
      return this.getSelectedOptions(this.audienceTargetingProperty, true)
    },
    excludedAudiences() {
      return this.getSelectedOptions(this.audienceTargetingProperty, false)
    },

    // Monetization
    monetizationTargetingProperty() {
      return this.wrapper && this.wrapper.targetingDict
        ? this.wrapper.targetingDict.monetizationproperty
        : null
    },
    includedWhiteList() {
      return this.getSelectedOptions(this.monetizationTargetingProperty, true)
    },
    includedBlackList() {
      return this.getSelectedOptions(this.monetizationTargetingProperty, false)
    },
    // Languages
    languageTargetingProperty() {
      return this.wrapper && this.wrapper.targetingDict
        ? this.wrapper.targetingDict.language
        : null
    },
    includedLanguages() {
      return this.getSelectedOptions(this.languageTargetingProperty, true)
    },
    excludedLanguages() {
      return this.getSelectedOptions(this.languageTargetingProperty, false)
    },
    subTypeCatalogs() {
      if (this.wrapper.catalogs == null) { return [] }
      return this.wrapper.catalogs.filter(x => x.subtypeMapped === true)
    },
    // targeting properties
    targetingPropertyType() {
      return {
        PREDEFINED: 1,
        FREEFORM: 2,
      }
    },
    minimumBidValue() {
      var { minimumBidValue } = this.wrapper.bidStrategy
      Object.keys(this.wrapper.targetingDict).forEach(property => {
        var propertyInfo = this.wrapper.targetingDict[property]
        var selectedOptions = this.getSelectedOptions(propertyInfo, true)
        var excludedOptions = this.getSelectedOptions(propertyInfo, false)
        if (selectedOptions.length > 0) {
          minimumBidValue += ((minimumBidValue * propertyInfo.bidIncrement) / 100)
        }
        if (excludedOptions.length > 0) {
          minimumBidValue += ((minimumBidValue * propertyInfo.bidIncrement) / 100)
        }
      })

      for (let i = 0; i < this.wrapper.catalogs.length; i += 1) {
        if (this.wrapper.catalogs[i].selectedItems.length > 0) {
          minimumBidValue += ((minimumBidValue * this.wrapper.catalogs[i].bidIncrement) / 100)
          break
        }
      }

      if (this.wrapper.model.specificLink != null && this.wrapper.model.specificLink !== '') {
        minimumBidValue += ((minimumBidValue * this.wrapper.model.specificLinkBidIncrement) / 100)
      }

      return ceilToCurrency(minimumBidValue)
    },
    maximumBidValue() {
      if (!this.wrapper.bidStrategy || !this.wrapper.bidStrategy.maximumBidValue) return null
      return floorToCurrency(this.wrapper.bidStrategy.maximumBidValue)
    },
    isFormComplete() {
      const {
        name, adTypeId, adSubtypeId, bidStrategyId,
      } = this.wrapper.model

      return name && adTypeId && adSubtypeId && bidStrategyId
    },
  },
  async mounted() {
    this.loading = true
    const campaignId = this.campaignId !== undefined ? this.campaignId : 0
    const adGroupId = this.adGroupId !== undefined ? this.adGroupId : 0
    try {
      this.wrapper = await AdGroupService.get(adGroupId, campaignId, this.$route.params.accountId)
      if (this.wrapper.surveyTargeting.length > 0) { this.surveyTargeting = this.wrapper.surveyTargeting }
    } catch (error) {
      this.error(error.message)
    } finally {
      this.loading = false
    }
  },
  methods: {
    /* eslint-disable no-nested-ternary,no-return-assign */
    // -- Start of checks for devices percentage region --
    adjustInput(value) {
      return value > 100 ? 100 : (value < 0 ? 0 : value)
    },
    filterOptions(privacyStatus) {
      const { optionGroups } = this.wrapper.targetingDict.survey
      return optionGroups[0].options.filter(option => option.privacyStatus === privacyStatus)
    },

    loadGeoTargetingOptions() {
      if (this.geoOptionGroups.length === 0 && this.geoTargetingProperty && this.geoTargetingProperty.optionGroups) {
        this.geoOptionGroups = this.geoTargetingProperty.optionGroups
      }
    },

    loadCityTargetingOptions() {
      if (this.cityTargetingOptions.length === 0 && this.cityTargetingProperty && this.cityTargetingProperty.optionGroups) {
        this.cityTargetingOptions = this.cityTargetingProperty.optionGroups
      }
    },

    loadOsTargetingOptions() {
      if (this.osOptionGroups.length === 0 && this.osTargetingProperty && this.osTargetingProperty.optionGroups) {
        this.osOptionGroups = this.osTargetingProperty.optionGroups
      }
    },

    loadBrowserTargetingOptions() {
      if (this.browserOptionGroups.length === 0 && this.browserTargetingProperty && this.browserTargetingProperty.optionGroups) {
        this.browserOptionGroups = this.browserTargetingProperty.optionGroups
      }
    },

    loadAudienceTargetingOptions() {
      if (this.audienceOptionGroups.length === 0 && this.audienceTargetingProperty && this.audienceTargetingProperty.optionGroups) {
        this.audienceOptionGroups = this.audienceTargetingProperty.optionGroups
      }
    },

    async loadCatalogItems(catalog) {
      if (catalog.availableItems.length === 0) {
        this.loadingCatalogItems = true
        try {
          const response = await CatalogsService.searchCatalogItems(catalog.id, '', this.$route.params.accountId)
          response.forEach(catalogItem => catalog.availableItems.push(catalogItem))
        } catch (error) {
          this.error(error.message)
        }
        this.loadingCatalogItems = false
      }
    },

    loadMonetizationTargetingOptions() {
      if (this.monetizationOptionGroups.length === 0 && this.monetizationTargetingProperty && this.monetizationTargetingProperty.optionGroups) {
        this.monetizationOptionGroups = this.monetizationTargetingProperty.optionGroups
      }
    },

    loadLanguageTargetingOptions() {
      if (this.languageOptionGroups.length === 0 && this.languageTargetingProperty && this.languageTargetingProperty.optionGroups) {
        this.languageOptionGroups = this.languageTargetingProperty.optionGroups
      }
    },

    loadSurveyTargetingOptions() {
      if (this.surveyTargetingProperty.length === 0 && this.wrapper && this.wrapper.targetingDict && this.wrapper.targetingDict.survey) {
        const privateOptions = this.filterOptions(SurveyPrivacyType.Private)
        const sharedOptions = this.filterOptions(SurveyPrivacyType.Shared)
        const publicOptions = this.filterOptions(SurveyPrivacyType.Public)

        this.surveyTargetingProperty = [
          { name: this.$tk('ClientPortal.Survey.SurveyPrivacyType.Private'), options: privateOptions },
          { name: this.$tk('ClientPortal.Survey.SurveyPrivacyType.Shared'), options: sharedOptions },
          { name: this.$tk('ClientPortal.Survey.SurveyPrivacyType.Public'), options: publicOptions },
        ]
      }
    },

    adjustselectedDevicesValue(enableCase = false) {
      const selectedDevices = this.deviceOptions.filter(e => e.selected === true)
      if (selectedDevices.length !== 3 || selectedDevices.length !== 0) {
        if (!enableCase) {
          const adjustedValue = {
            1: 100,
            2: 50,
          }[selectedDevices.length]
          selectedDevices.forEach(device => {
            this.deviceBidAdjustments[device.id].value = adjustedValue
          })
          this.currentTotalDevicePercent = 100
        }
      }
    },
    checkActiveDevices(deviceKey) {
      const selectedDevices = this.deviceOptions.filter(e => e.selected === true)
      if (selectedDevices.length === 1) {
        this.deviceBidAdjustments[deviceKey].value = null
        setTimeout(() => this.deviceBidAdjustments[deviceKey].value = 100, 1)
        this.currentTotalDevicePercent = 100
      }
    },
    checkTotalValueSelected(deviceIndex, deviceKey, deviceValue) {
      if (deviceValue > 100) { deviceValue = 100 }
      if (deviceValue < 0) { deviceValue = 0 }

      deviceValue = Math.round(deviceValue)
      this.deviceBidAdjustments[deviceKey].value = deviceValue
      const deviceEdited = { index: deviceIndex, id: deviceKey, timesEdited: 1 }
      if (!this.devicesModified.some(e => e.id === deviceEdited.id)) {
        this.devicesModified.push(deviceEdited)
      } else {
        const deviceEditedIndex = this.devicesModified.findIndex(e => e.id === deviceEdited.id)
        this.devicesModified[deviceEditedIndex].timesEdited += 1
      }

      const checkResult = this.checkIfDevicesValueIsAcceptable()
      if (!checkResult && deviceValue !== 0) {
        this.fillOtherFields(deviceIndex, deviceKey, deviceValue)
      }
    },
    fillOtherFields(index, deviceKey, deviceValue) {
      const devicesObserver = this.deviceBidAdjustments
      const excludedDevices = this.devicesToExclude()
      const nrOfOtherFields = {
        0: 2,
        1: 1,
        2: 0,
      }[excludedDevices.length]
      const devices = Object.keys(devicesObserver)
      let counterForRoundCase = 0
      devices.forEach(key => {
        const currentKey = parseInt(key, 10)
        if (excludedDevices.length < 2) {
          if (currentKey !== deviceKey && !(excludedDevices[0] === currentKey || excludedDevices[1] === currentKey || excludedDevices[2] === currentKey)) {
            if (this.devicesModified[0].timesEdited > 0 && this.devicesModified[0].id === deviceKey) {
              if (this.devicesModified[0].count > 1) {
                this.currentTotalDevicePercent = 0
              }
              if (counterForRoundCase === 0) {
                this.deviceBidAdjustments[key].value = Math.ceil((100 - deviceValue) / nrOfOtherFields)
                counterForRoundCase += 1
              } else {
                this.deviceBidAdjustments[key].value = Math.floor((100 - deviceValue) / nrOfOtherFields)
                counterForRoundCase = 0
              }
              this.currentTotalDevicePercent = deviceValue
            } else if (this.devicesModified[0].id !== currentKey && excludedDevices.length === 0) {
              if (this.devicesModified[1].timesEdited > 0 && this.devicesModified[1].id === deviceKey) {
                if (this.devicesModified[1].timesEdited > 1) {
                  this.currentTotalDevicePercent = this.deviceBidAdjustments[this.devicesModified[0].id].value
                }
                this.currentTotalDevicePercent += deviceValue
                if ((100 - this.currentTotalDevicePercent) < 0) {
                  this.deviceBidAdjustments[key].value = 0
                  this.deviceBidAdjustments[this.devicesModified[0].id].value -= (this.currentTotalDevicePercent - 100)
                } else {
                  this.deviceBidAdjustments[key].value = ((100 - this.currentTotalDevicePercent))
                }
              } else if (this.devicesModified[2].timesEdited > 0 && this.devicesModified[2].id === deviceKey) {
                this.currentTotalDevicePercent = this.deviceBidAdjustments[this.devicesModified[0].id].value
                this.currentTotalDevicePercent += deviceValue
                if ((100 - this.currentTotalDevicePercent) < 0) {
                  this.deviceBidAdjustments[key].value = 0
                  this.deviceBidAdjustments[this.devicesModified[0].id].value -= (this.currentTotalDevicePercent - 100)
                } else {
                  this.deviceBidAdjustments[key].value = ((100 - this.currentTotalDevicePercent))
                }
              }
            } else if (excludedDevices.length === 1) {
              if (this.devicesModified[1].timesEdited > 0 && this.devicesModified[1].id === deviceKey) {
                this.currentTotalDevicePercent = 0
                this.currentTotalDevicePercent += deviceValue
                this.deviceBidAdjustments[key].value = ((100 - this.currentTotalDevicePercent))
              }
            }
          }
        } else if (excludedDevices.length === 2 && currentKey === deviceKey) {
          this.deviceBidAdjustments[key].value = 100
          this.currentTotalDevicePercent = 100
        }
      })
      return this.currentTotalDevicePercent
    },
    checkIfDevicesValueIsAcceptable() {
      let totalValue = 0
      const devicesObserver = this.deviceBidAdjustments
      const devices = Object.keys(devicesObserver)
      devices.forEach(key => {
        const device = devicesObserver[key]
        totalValue += device.value
      })
      return totalValue === 100
    },
    devicesToExclude() {
      const devicesOptionObserver = this.deviceOptions
      const devicesOption = Object.keys(devicesOptionObserver)
      const result = []
      devicesOption.forEach(key => {
        const device = devicesOptionObserver[key]
        const deviceSel = device.selected
        if (!deviceSel) {
          result.push(device.id)
        }
      })
      return result
    },
    // -- End of checks for devices percentage region --
    customLabel({ option }) {
      return `${option}`
    },
    getAdTypeButtonClass(adTypeId) {
      return this.getButtonClass(adTypeId === this.wrapper.adType.id)
    },
    getAdSubtypeButtonClass(adSubtypeId) {
      return this.getButtonClass(adSubtypeId === this.wrapper.model.adSubtypeId)
    },
    getAdSubtypeTextClass(adSubtypeId) {
      return adSubtypeId === this.wrapper.model.adSubtypeId ? 'text-primary' : ''
    },
    getButtonClass(active) {
      if (active) return 'border-primary text-primary'
      return 'border'
    },
    getAdSubtypeBidStrategyButtonClass(bidStrategyId) {
      return this.getButtonClass(bidStrategyId === this.wrapper.model.bidStrategyId)
    },
    setType(adType) {
      this.adType = adType
      const { wrapper } = this
      wrapper.adType = adType
      wrapper.model.adTypeId = adType.id
      wrapper.model.adSubtypeId = 0
      wrapper.model.bidStrategyId = 0
    },
    async setSubtype(adSubtype) {
      this.wrapper.adSubtype = adSubtype
      this.wrapper.model.adSubtypeId = adSubtype.id
      this.wrapper.model.bidStrategyId = 0
      if (this.adSubtype) {
        this.wrapper = await AdGroupService.get(this.adGroupId, this.campaignId, this.$route.params.accountId)
        this.wrapper.adType = this.adType
        this.wrapper.model.adTypeId = this.adType.id
      }
      this.wrapper.adSubtype = adSubtype
      this.wrapper.model.adSubtypeId = adSubtype.id
      this.adSubtype = adSubtype

      this.checkSubtypeCatalogs()
      this.checkSubtypeTargetingProperties()
    },
    setSubtypeBidStrategy(subtypeBidStrategy) {
      const { wrapper } = this
      wrapper.model.bidStrategyId = subtypeBidStrategy.bidStrategy.id
      wrapper.bidStrategy = subtypeBidStrategy
      wrapper.model.bidValue = this.minimumBidValue
      if (this.maximumBidValue && wrapper.model.bidValue > this.maximumBidValue) {
        wrapper.model.bidValue = this.maximumBidValue
      }
      setTimeout(() => this.optionsLimit = 1000, 2000)
    },
    removeSurvey(survey) {
      let removedSurvey = {}
      this.surveyTargetingProperty.forEach(element => {
        const surveyToRemove = element.options.find(x => x.id === survey.id)
        if (surveyToRemove) {
          removedSurvey = surveyToRemove
        }
      })
      removedSurvey.selected = false
      removedSurvey.include = false
      this.surveyTargeting.forEach(element => {
        if (element.id === survey.id) {
          const surveyIndex = this.surveyTargeting.indexOf(element)
          this.surveyTargeting.splice(surveyIndex, 1)
        }
      })
    },
    addFrequencyCap() {
      this.wrapper.model.frequencyCaps.push({
        frequencyCapEntityId: 2,
        frequencyCapEventId: 1,
        frequencyCapPeriodId: 1,
        frequencyCapValue: 1,
      })
    },
    removeFrequencyCap(index) {
      this.wrapper.model.frequencyCaps.splice(index, 1)
    },
    copyFrequencyCap(frequencyCap) {
      this.wrapper.model.frequencyCaps.push({
        frequencyCapEntityId: frequencyCap.frequencyCapEntityId,
        frequencyCapEventId: frequencyCap.frequencyCapEventId,
        frequencyCapPeriodId: frequencyCap.frequencyCapPeriodId,
        frequencyCapValue: frequencyCap.frequencyCapValue,
      })
    },
    getSelectedOptions(property, include) {
      const options = []
      const { optionGroups } = property
      for (let i = 0; i < optionGroups.length; i += 1) {
        for (let j = 0; j < optionGroups[i].options.length; j += 1) {
          const option = optionGroups[i].options[j]
          if (option.selected && option.include === include) {
            options.push(option)
          }
        }
      }
      return options
    },
    // targeting
    includeTargetingOption(option) {
      option.selected = true
      option.include = true
    },

    excludeTargetingOption(option) {
      option.selected = true
      option.include = false
    },
    // ad scheduling
    addSchedule() {
      this.wrapper.model.schedules.push({
        typeId: 1,
        hourFrom: '00:00 AM',
        hourTo: '11:59 PM',
      })
    },
    removeSchedule(index) {
      this.wrapper.model.schedules.splice(index, 1)
    },
    copySchedule(schedule) {
      this.wrapper.model.schedules.push({
        typeId: schedule.typeId,
        hourFrom: schedule.hourFrom,
        hourTo: schedule.hourTo,
      })
    },
    getAdTypeIconTemporarily(adType) {
      switch (adType.id) {
        case 1:
          return 'Desktop'
        case 2:
          return 'Video'
        case 3:
          return 'Devices'
        default:
          return ''
      }
    },
    getBidValueText() {
      let text = `${this.$tk('ClientPortal.Form.Price.MinimumBidValueIs')}: ${formatCurrency(this.minimumBidValue)}`

      if (this.maximumBidValue) {
        text += `<br>${this.$tk('ClientPortal.Form.Price.MaximumBidValueIs')}: ${formatCurrency(this.maximumBidValue)}`
      }

      return text
    },
    getAdSubtypeIcon(adSubtype) {
      switch (adSubtype.id) {
        case 1:
          // eslint-disable-next-line global-require
          return require('@/assets/images/illustration/image.svg')
        case 2:
          // eslint-disable-next-line global-require
          return require('@/assets/images/illustration/html.svg')
        case 3:
          // eslint-disable-next-line global-require
          return require('@/assets/images/illustration/external.svg')
        case 4:
          // eslint-disable-next-line global-require
          return require('@/assets/images/illustration/bumper.svg')
        case 5:
          // eslint-disable-next-line global-require
          return require('@/assets/images/illustration/skippable.svg')
        case 6:
          // eslint-disable-next-line global-require
          return require('@/assets/images/illustration/product.svg')
        case 7:
          // eslint-disable-next-line global-require
          return require('@/assets/images/illustration/under-video.svg')
        case 8:
          // eslint-disable-next-line global-require
          return require('@/assets/images/illustration/survey.svg')
        case 9:
          // eslint-disable-next-line global-require
          return require('@/assets/images/illustration/unskippable.svg')
        case 10:
          // eslint-disable-next-line global-require
          return require('@/assets/images/illustration/chat.svg')
        case 11:
          // eslint-disable-next-line global-require
          return require('@/assets/images/illustration/outstream.svg')
        case 12:
          // eslint-disable-next-line global-require
          return require('@/assets/images/illustration/companion.svg')
        case 13:
          // eslint-disable-next-line global-require
          return require('@/assets/images/illustration/companion.svg')
        case 14:
          // eslint-disable-next-line global-require
          return require('@/assets/images/illustration/outstream.svg')
        case 15:
          // eslint-disable-next-line global-require
          return require('@/assets/images/illustration/anchor.svg')
        case 16:
          // eslint-disable-next-line global-require
          return require('@/assets/images/illustration/parallax.svg')
        default:
          return ''
      }
    },
    checkTargetingOption(option, deviceCase = false) {
      if (option.selected) {
        option.selected = false
        option.include = false
        if (deviceCase) {
          const deviceEditedIndex = this.devicesModified.findIndex(e => e.id === option.id)
          this.devicesModified.splice(deviceEditedIndex, 1)
          this.adjustselectedDevicesValue()
        }
      } else {
        option.selected = true
        option.include = true
        if (deviceCase) {
          this.checkActiveDevices(option.id)
        }
      }
      if (this.deviceBidAdjustments[option.id] != null) this.deviceBidAdjustments[option.id].value = 0
    },
    removeTargetingOption(option) {
      option.selected = false
      option.include = false
    },
    checkSubtypeCatalogs() {
      for (let i = 0; i < this.wrapper.catalogs.length; i += 1) {
        const catalog = this.wrapper.catalogs[i]
        if (catalog.subtypesList.includes(this.wrapper.model.adSubtypeId)) {
          catalog.subtypeMapped = true
        } else {
          catalog.subtypeMapped = false
        }
      }
    },
    checkSubtypeTargetingProperties() {
      for (let i = 0; i < this.wrapper.targetingProperties.length; i += 1) {
        const targetingProperty = this.wrapper.targetingProperties[i]
        if (targetingProperty.subtypesList.includes(this.wrapper.model.adSubtypeId)) {
          targetingProperty.subtypeMapped = true
        } else {
          targetingProperty.subtypeMapped = false
        }
      }
    },
    // catalogs
    async addCatalog(catalog) {
      catalog.show = true
      this.loadingCatalogItems = true
      try {
        const response = await CatalogsService.searchCatalogItems(catalog.id, '', this.$route.params.accountId)
        response.forEach(catalogItem => catalog.availableItems.push(catalogItem))
      } catch (error) {
        this.error(error.message)
      }
      this.loadingCatalogItems = false
    },
    removeCatalog(catalog) {
      catalog.show = false
      this.removeAllCatalogItems(catalog)
    },
    removeAllCatalogItems(catalog) {
      catalog.selectedItems.splice(0, catalog.selectedItems.length)
    },
    async searchCatalogItems(catalog, query) {
      var $this = this
      if ($this.loadingCatalogItems) return

      $this.loadingCatalogItems = true
      catalog.availableItems.splice(0, catalog.availableItems.length)
      try {
        const response = await CatalogsService.searchCatalogItems(catalog.id, query, this.$route.params.accountId)
        response.forEach(catalogItem => catalog.availableItems.push(catalogItem))
      } catch (error) {
        this.error(error.message)
      }
      $this.loadingCatalogItems = false
    },
    addCatalogItem(catalog, catalogItem, include) {
      var catalogItemIndex = catalog.selectedItems.findIndex(c => c.id === catalogItem.id)
      if (catalogItemIndex === -1) {
        catalogItem.include = include
        catalog.selectedItems.push(catalogItem)
      } else {
        catalog.selectedItems[catalogItemIndex].include = include
      }
    },
    removeCatalogItem(catalog, catalogItemId) {
      const catalogItemIndex = catalog.selectedItems.findIndex(x => x.id === catalogItemId)
      catalog.selectedItems.splice(catalogItemIndex, 1)
    },
    // custom targeting properties
    addTargetingProperty(targetingProperty) {
      targetingProperty.show = true
    },
    removeTargetingProperty(index) {
      const targetingProperty = this.wrapper.targetingProperties[index]
      targetingProperty.show = false
      this.removeAllTargetingOptions(targetingProperty)
    },
    addTargetingOption(targetingProperty, targetingOption, include) {
      var targetingOptionIndex = targetingProperty.selectedOptions.findIndex(x => x.id === targetingOption.id)
      if (targetingOptionIndex === -1) {
        targetingOption.include = include
        targetingProperty.selectedOptions.push(targetingOption)
      } else {
        targetingProperty.selectedOptions[targetingOptionIndex].include = include
      }
    },
    addNewTargetingOption(targetingProperty, option, include) {
      var targetingOptionIndex = targetingProperty.selectedOptions.findIndex(x => x.value === option.toLowerCase())
      if (targetingOptionIndex === -1) {
        targetingProperty.selectedOptions.push({ name: option, value: option.toLowerCase(), include })
      } else {
        targetingProperty.selectedOptions[targetingOptionIndex].include = include
      }
    },
    removeCustomTargetingOption(targetingProperty, targetingOptionIndex) {
      const optionIndex = targetingProperty.selectedOptions.findIndex(option => option.id === targetingOptionIndex.id)
      targetingProperty.selectedOptions.splice(optionIndex, 1)
    },
    removeAllTargetingOptions(targetingProperty) {
      targetingProperty.selectedOptions.splice(0, targetingProperty.selectedOptions.length)
    },
    getSpecificLinkByType(type, value) {
      if (type === 1) {
        value = `${value}%`
      } else if (type === 2) {
        value = `%${value}`
      } else if (type === 3) {
        value = `%${value}%`
      }
      return value
    },
    async saveAdGroup() {
      var $this = this
      var monetizationPropertyId = $this.wrapper.targetingDict.monetizationproperty.id
      var surveyPropertyId = $this.wrapper.targetingDict.survey.id
      var audiencePropertyId = $this.wrapper.targetingDict.audience.id
      var targetingValues = []
      this.saving = true

      Object.keys($this.wrapper.targetingDict).forEach(propertyName => {
        const property = $this.wrapper.targetingDict[propertyName]
        var { optionGroups } = property
        for (let i = 0; i < optionGroups.length; i += 1) {
          for (let j = 0; j < optionGroups[i].options.length; j += 1) {
            const option = optionGroups[i].options[j]
            if (option.selected) {
              if (option.propertyId === monetizationPropertyId
                                || option.propertyId === audiencePropertyId) {
                targetingValues.push({
                  propertyId: option.propertyId,
                  customValue: option.id.toString(),
                  optionId: null,
                  include: option.include,
                })
              } else if (option.propertyId === surveyPropertyId) {
                this.surveyTargeting.forEach(element => {
                  element.selectedAnswers.forEach(e => {
                    targetingValues.push({
                      propertyId: option.propertyId,
                      customValue: e.id,
                      optionId: null,
                      include: option.include,
                    })
                  })
                })
              } else {
                targetingValues.push({
                  propertyId: option.propertyId,
                  optionId: option.id,
                  include: option.include,
                })
              }
            }
          }
        }
      })

      for (let i = 0; i < $this.wrapper.catalogs.length; i += 1) {
        const catalog = $this.wrapper.catalogs[i]
        if (catalog.subtypeMapped) {
          for (let j = 0; j < catalog.selectedItems.length; j += 1) {
            const catalogItem = catalog.selectedItems[j]
            targetingValues.push({
              propertyId: catalog.targetingPropertyId,
              customValue: catalogItem.id.toString(),
              optionId: null,
              include: catalogItem.include,
            })
          }
        }
      }

      for (let i = 0; i < $this.wrapper.targetingProperties.length; i += 1) {
        const targetingProperty = $this.wrapper.targetingProperties[i]
        if (targetingProperty.subtypeMapped) {
          for (let j = 0; j < targetingProperty.selectedOptions.length; j += 1) {
            const targetingOption = targetingProperty.selectedOptions[j]

            if (targetingProperty.type === $this.targetingPropertyType.PREDEFINED) {
              targetingValues.push({
                propertyId: targetingProperty.id,
                optionId: targetingOption.id,
                include: targetingOption.include,
              })
            } else {
              targetingValues.push({
                propertyId: targetingProperty.id,
                optionId: null,
                customValue: targetingOption.value,
                include: targetingOption.include,
              })
            }
          }
        }
      }

      if ($this.wrapper.model.specificLink != null && $this.wrapper.model.specificLinkType > 0) {
        if ($this.wrapper.model.specificLink !== '') {
          const customValue = this.getSpecificLinkByType($this.wrapper.model.specificLinkType, $this.wrapper.model.specificLink)
          targetingValues.push({
            propertyId: $this.wrapper.model.specificLinkPropertyId,
            customValue,
            optionId: null,
            include: true,
          })
        }
      }

      $this.wrapper.model.targetingValues = targetingValues
      const { model } = $this.wrapper
      const { accountId } = this.$route.params
      const isEdit = $this.wrapper.model.id > 0
      try {
        const response = await AdGroupService.create(model, accountId)
        if (response.success) {
          if (isEdit) {
            this.success(response.message)
            if (this.entity) {
              this.editAdGroupEntity(this.entity, model)
            } else {
              this.$router.push(`/${accountId}/?tab=adgroups`)
            }
          } else if (response.success) {
            this.success(response.message)
            this.$router.push(`/${accountId}/ad/create?adGroupId=${response.id}`)
          }
        } else {
          this.error(response.message)
          this.errors = response.errors
        }
      } catch (error) {
        this.error(error.message)
      }
      scrollToError(this.errors)
      this.saving = false
    },
    editAdGroupEntity(entity, adGroup) {
      entity.name = adGroup.name
      this.closeEditModal()
    },
    async getAdGroup(adGroupId, campaignId) {
      this.surveyTargeting = this.wrapper.surveyTargeting
      this.wrapper = await AdGroupService.get(adGroupId, campaignId, this.$route.params.accountId)
    },
    cancelAdGroup() {
      // this.$router.push(`/${this.$route.params.accountId}/adgroup/create`)
      this.$router.push(`/${this.$route.params.accountId}`)
    },
    cancelAdGroupEdit() {
      this.$router.push(`/${this.$route.params.accountId}?tab=adgroups`)
    },
    showConfirmationModal() {
      this.$refs['navigation-modal'].show()
      return new Promise(resolve => {
        this.$once('confirmed', () => {
          resolve()
        })
      })
    },
    confirm() {
      this.$emit('confirmed')
    },
  },
  beforeRouteLeave(to, from, next) {
    const handlers = this.wrapper.model.name
      || this.adType
      || this.campaignId
    if (handlers && !this.saving) {
      this.showConfirmationModal().then(() => {
        next()
      }).catch(() => {
        next(false)
      })
    } else {
      next()
    }
  },
  beforeRouteUpdate(to, from, next) {
    var campaignId = to.query.campaignId !== undefined ? to.query.campaignId : 0
    this.getAdGroup(0, campaignId)
    next()
  },
  setup(_, context) {
    const surveyTargeting = ref([])
    const getSurveyDetails = async option => {
      try {
        const response = await SurveysService.getSearchSurveyTargetingDetails(option.id, context.root.$route.params.accountId)

        surveyTargeting.value.push({
          id: option.id,
          surveyName: option.option,
          selectedSurvey: response,
          selectedAnswers: [],
          selectedQuestions: [],
          privacyStatus: option.privacyStatus,
        })
      } catch (error) {
        notification.methods.error(error.message)
      }
    }
    const includeSurveyAnswers = (option, id) => {
      surveyTargeting.value.forEach(element => {
        if (element.id === id) {
          if (!element.selectedAnswers.find(answer => answer.id === option.id)) {
            element.selectedAnswers.push(option)
          } else {
            const optionToDelete = element.selectedAnswers.findIndex(answer => answer.id === option.id)
            element.selectedAnswers.splice(optionToDelete, 1)
          }
          if (!element.selectedQuestions.includes(option.questionId)) {
            element.selectedQuestions.push(option.questionId)
          } else if (!element.selectedAnswers.some(a => a.questionId === option.questionId)) {
            const questionIndex = element.selectedQuestions.indexOf(option.questionId)
            element.selectedQuestions.splice(questionIndex, 1)
          }
        }
      })
    }

    const includeSurveyTargetingOption = option => {
      if (!option.selected) {
        option.selected = true
        option.include = true
        getSurveyDetails(option)
      }
    }

    return {
      includeSurveyAnswers,
      includeSurveyTargetingOption,
      surveyTargeting,
    }
  },
}
</script>

<style lang="scss">
@import '@/assets/scss/libs/_vue-multiselect.scss';
@import '@/assets/scss/views/client/ads-manager/create/ad-group.scss';
</style>
